import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static targets = ["form", "result", "amount", "supplier", "costCenter"]
  static values = {
    url: String
  }

  connect() {
    this.resultTarget.classList.add("d-none")
  }

  async test(event) {
    event.preventDefault()

    const formData = new FormData(this.formTarget)
    const data = {
      test_amount: formData.get("test_amount"),
      test_supplier_id: formData.get("test_supplier_id"),
      test_cost_center: formData.get("test_cost_center")
    }

    try {
      this.resultTarget.innerHTML = this.loadingHtml()
      this.resultTarget.classList.remove("d-none")

      const response = await post(this.urlValue, {
        body: JSON.stringify(data),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const result = await response.json
        this.displayResult(result)
      } else {
        this.displayError("Failed to test rules")
      }
    } catch (error) {
      console.error("Error testing rules:", error)
      this.displayError("An error occurred while testing rules")
    }
  }

  displayResult(result) {
    if (result.matched) {
      this.resultTarget.innerHTML = this.matchedHtml(result)
    } else {
      this.resultTarget.innerHTML = this.noMatchHtml(result)
    }
  }

  displayError(message) {
    this.resultTarget.innerHTML = `
      <div class="alert alert-danger mb-0">
        <span class="material-symbols-outlined me-2">error</span>
        ${this.escapeHtml(message)}
      </div>
    `
  }

  loadingHtml() {
    return `
      <div class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Testing...</span>
        </div>
        <span class="ms-2">Testing rules...</span>
      </div>
    `
  }

  matchedHtml(result) {
    let html = `
      <div class="alert alert-success mb-0">
        <div class="d-flex align-items-center mb-2">
          <span class="material-symbols-outlined me-2">check_circle</span>
          <strong>Match found!</strong>
        </div>
    `

    if (result.auto_approve) {
      html += `
        <div class="mt-2 p-2 bg-white rounded">
          <span class="badge badge--success me-2">Auto-approve</span>
          This expense would be automatically approved.
        </div>
      `
    } else {
      html += `
        <div class="mt-2 p-2 bg-white rounded">
          <strong>Rule:</strong> ${this.escapeHtml(result.rule_name)}<br>
          <strong>Workflow:</strong> ${this.escapeHtml(result.workflow_name)}<br>
          <strong>Levels:</strong> ${result.levels_count}
        </div>
      `
    }

    html += "</div>"
    return html
  }

  noMatchHtml(result) {
    let html = `
      <div class="alert alert-warning mb-0">
        <div class="d-flex align-items-center mb-2">
          <span class="material-symbols-outlined me-2">info</span>
          <strong>No specific rule matched</strong>
        </div>
    `

    if (result.default_workflow) {
      html += `
        <div class="mt-2 p-2 bg-white rounded">
          The expense would use the default workflow: <strong>${this.escapeHtml(result.default_workflow)}</strong>
        </div>
      `
    } else {
      html += `
        <div class="mt-2 p-2 bg-white rounded text-danger">
          <span class="material-symbols-outlined me-1" style="font-size: 1rem;">warning</span>
          No matching workflow found. Set a default workflow or create matching rules.
        </div>
      `
    }

    html += "</div>"
    return html
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  reset() {
    this.formTarget.reset()
    this.resultTarget.classList.add("d-none")
  }
}
